// Reports API using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getAccessToken } from './auth-token'
import { getSchoolContext } from './school-context'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

export interface TeacherWorkloadData {
  teacher_id: string
  teacher_name: string
  total_periods: number
  free_periods: number
}

export interface SubjectAllocationData {
  subject_id: string
  subject_name: string
  subject_code: string
  total_periods: number
  percentage: number
}

export interface FreePeriodData {
  teacher_id: string
  teacher_name: string
  total_free_periods: number
  monday_free: number
  tuesday_free: number
  wednesday_free: number
  thursday_free: number
  friday_free: number
}

export interface SubstitutionStatsData {
  teacher_id: string
  teacher_name: string
  total_substitutions: number
  auto_assignments: number
  manual_assignments: number
  avg_response_time_minutes: number
}

export interface TimetableVersion {
  valid_from: string
  valid_to: string | null
}

async function resolveSchoolUuid(accessToken: string, schoolCode: string): Promise<string> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/schools?school_id=eq.${schoolCode}&select=id`,
    {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  )
  if (!response.ok) throw new Error('Failed to resolve school UUID')
  const data: { id: string }[] = await response.json()
  if (!data[0]?.id) throw new Error('School not found')
  return data[0].id
}

async function callVersionedRpc<T>(
  rpcName: string,
  academicYearId?: string,
  versionValidFrom?: string
): Promise<T[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolCode = getSchoolContext()
    if (!schoolCode) throw new Error('No school context')

    const schoolUuid = await resolveSchoolUuid(accessToken, schoolCode)

    // Resolve academic year text name from UUID
    let academicYearText: string | null = null
    if (academicYearId) {
      const ayResp = await fetch(
        `${SUPABASE_URL}/rest/v1/academic_years?id=eq.${academicYearId}&select=name`,
        { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
      )
      if (ayResp.ok) {
        const ayData: { name: string }[] = await ayResp.json()
        academicYearText = ayData[0]?.name ?? null
      }
    }

    const body = {
      p_school_id: schoolUuid,
      p_academic_year: academicYearText ?? null,
      p_version_valid_from: versionValidFrom ?? null,
    }
    console.log(`[RPC] ${rpcName} params:`, body)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${rpcName}`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const error = await response.json()
      console.error(`[RPC] ${rpcName} error:`, error)
      throw new Error(error.message || `Failed to fetch ${rpcName}`)
    }

    const result = await response.json()
    console.log(`[RPC] ${rpcName} result (${result.length} rows):`, result.slice(0, 2))
    return result as T[]
  })
}

async function callDateRangeRpc<T>(
  rpcName: string,
  startDate?: string,
  endDate?: string
): Promise<T[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolCode = getSchoolContext()
    if (!schoolCode) throw new Error('No school context')

    const schoolUuid = await resolveSchoolUuid(accessToken, schoolCode)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${rpcName}`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        p_school_id: schoolUuid,
        p_start_date: startDate ?? null,
        p_end_date: endDate ?? null,
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || `Failed to fetch ${rpcName}`)
    }

    return response.json() as Promise<T[]>
  })
}

/** Fetch distinct timetable versions (valid_from / valid_to) for a given academic year UUID */
export async function getTimetableVersionsDirect(academicYearId: string): Promise<TimetableVersion[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolCode = getSchoolContext()
    if (!schoolCode) throw new Error('No school context')

    const params = new URLSearchParams({
      select: 'valid_from,valid_to',
      school_id: `eq.${schoolCode}`,
      academic_year_id: `eq.${academicYearId}`,
      superseded_by: 'is.null',
      order: 'valid_from.asc',
    })

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${params}`, {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        Prefer: 'return=representation',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch timetable versions')
    }

    const rows: TimetableVersion[] = await response.json()

    // Deduplicate by valid_from
    const seen = new Set<string>()
    return rows.filter(r => {
      if (seen.has(r.valid_from)) return false
      seen.add(r.valid_from)
      return true
    })
  })
}

export async function getTeacherWorkloadDirect(academicYearId?: string, versionValidFrom?: string): Promise<TeacherWorkloadData[]> {
  return callVersionedRpc<TeacherWorkloadData>('get_teacher_workload', academicYearId, versionValidFrom)
}

export async function getSubjectAllocationDirect(academicYearId?: string, versionValidFrom?: string): Promise<SubjectAllocationData[]> {
  return callVersionedRpc<SubjectAllocationData>('get_subject_allocation', academicYearId, versionValidFrom)
}

export async function getTeacherFreePeriodsDirectReport(academicYearId?: string, versionValidFrom?: string): Promise<FreePeriodData[]> {
  return callVersionedRpc<FreePeriodData>('get_teacher_free_periods', academicYearId, versionValidFrom)
}

export async function getSubstitutionStatsDirect(startDate?: string, endDate?: string) {
  return callDateRangeRpc<SubstitutionStatsData>('get_substitution_stats', startDate, endDate)
}
